import {
  Attachment,
  AttachmentFile,
  AttachmentFileBase,
  Conversation,
  DocumentFile,
  Id,
  MediaFile,
  Mention,
  Message,
  Reaction,
  User,
} from '@nextforce/api'
import { Image } from '@rneui/themed'
import { ChatConfig } from 'chat/config/types'
import { AccessoryButtonName } from 'chat/messages/views/accessory_bar'

import * as FileSystem from 'expo-file-system/legacy'
import { cloneDeep } from 'lodash'
import { ImageChangeEventData, Platform } from 'react-native'
import { MAX_MESSAGE_LENGTH, WEB_SCREEN_WIDTH } from 'shared/constants'

import { t } from 'app/i18l'
import { INITIAL_MESSAGE_FETCH_WINDOW_DAYS } from 'chat/messages/queries/list'
import { isGifType, isImageType, isPdfType } from 'file_folder_repository/util'
import moment from 'moment'
import { useMemo } from 'react'
import { informationAlert } from 'shared/alerts'
import { DocumentResult, pickDocument } from 'shared/document_picker'
import {
  MediaResult,
  pickImageFromLibrary,
  takePhotoWithCamera,
} from 'shared/image_picker'
import ImageCropPicker from 'shared/wrappers/react-native-image-crop-picker'
import {
  DEFAULT_NEWSFEED_IMAGE_HEIGHT,
  GROUP_MEDIA_TIME_RANGE_MINS,
  NEWSFEED_CARD_PADDING,
} from './constants'

interface AddMessageAttachmentProps {
  chatGroupId: string
  type: AccessoryButtonName
  chatConfig: ChatConfig | undefined
  setSelectedAttachments: (
    value: React.SetStateAction<Record<string, AttachmentFile[]>>
  ) => void
}

export const addMessageAttachment = async ({
  chatGroupId,
  type,
  chatConfig,
  setSelectedAttachments,
}: AddMessageAttachmentProps) => {
  let mediaResult: MediaResult | DocumentResult
  switch (type) {
    case 'photo':
      mediaResult = await pickImageFromLibrary({
        maxVideoDuration: chatConfig?.maxVideoDuration,
        allowsMultipleSelection: true,
      })
      break
    case 'camera':
      mediaResult = await takePhotoWithCamera()
      break
    case 'upload':
      mediaResult = await pickDocument()
  }
  if (mediaResult.status === 'success') {
    const attachmentFiles: AttachmentFile[] = mediaResult.files
    setSelectedAttachments(prevState => ({
      ...prevState,
      [chatGroupId]: prevState[chatGroupId]
        ? [...prevState[chatGroupId], ...attachmentFiles]
        : attachmentFiles,
    }))
  }
}

interface RemoveMessageAttachmentProps {
  chatGroupId: string
  uri: string
  setSelectedAttachments: (
    value: React.SetStateAction<Record<string, AttachmentFile[]>>
  ) => void
}

export const removeMessageAttachment = ({
  chatGroupId,
  uri,
  setSelectedAttachments,
}: RemoveMessageAttachmentProps) => {
  setSelectedAttachments(prevState => ({
    ...prevState,
    [chatGroupId]: prevState[chatGroupId]?.filter(
      attachment => attachment.uri !== uri
    ),
  }))
}

interface EditMessageAttachmentProps {
  chatGroupId: string
  uri: string
  selectedAttachments: Record<string, AttachmentFile[]>
  setSelectedAttachments: (
    value: React.SetStateAction<Record<string, AttachmentFile[]>>
  ) => void
}
export const editMessageAttachment = async ({
  chatGroupId,
  uri,
  selectedAttachments,
  setSelectedAttachments,
}: EditMessageAttachmentProps) => {
  const attachmentIndex = selectedAttachments[chatGroupId]?.findIndex(
    selectedAttachment => selectedAttachment.uri === uri
  )
  if (attachmentIndex !== -1) {
    const attachment = selectedAttachments[chatGroupId][attachmentIndex]
    if (isImageType(attachment.mimeType) && !isGifType(attachment.mimeType)) {
      const editedImage = await ImageCropPicker.openCropper({
        mediaType: 'photo',
        path: attachment.uri,
        freeStyleCropEnabled: true,
        ...(Platform.OS === 'ios'
          ? { width: attachment.width, height: attachment.height }
          : {}),
      }).catch(e => null)

      if (editedImage) {
        const editedAttachment = cloneDeep(attachment)
        editedAttachment.height = editedImage.height
        editedAttachment.width = editedImage.width
        editedAttachment.uri = editedImage.path

        const newAttachments = [...selectedAttachments[chatGroupId]]
        newAttachments[attachmentIndex] = editedAttachment

        setSelectedAttachments(prevState => ({
          ...prevState,
          [chatGroupId]: newAttachments,
        }))
      }
    }
  }
}

export const isFirstAttachmentMessage = (
  messageId: Id,
  messages: Message[] | readonly Message[]
) => {
  if (messages) {
    const currentAttachmentMessageIndex = messages.findIndex(
      m => m.id === messageId
    )
    const currentMessage = messages[currentAttachmentMessageIndex]

    if (currentAttachmentMessageIndex > 0) {
      const prevMessage = messages[currentAttachmentMessageIndex - 1]
      const isNotFirstAttachmentMessage =
        !currentMessage.body &&
        currentMessage.attachment &&
        prevMessage.attachment &&
        prevMessage.user.id === currentMessage.user.id &&
        moment(currentMessage.createdAt).diff(
          prevMessage.createdAt,
          'minutes'
        ) < GROUP_MEDIA_TIME_RANGE_MINS
      if (isNotFirstAttachmentMessage) {
        return false
      }
    }
    return true
  }
}

type GroupedMessageResult = {
  groupedMessages: Message[][]
  messageIdToGroupIndex: Map<Id, number>
}

export function groupMessages(
  messages: readonly Message[] | undefined
): GroupedMessageResult {
  const groupedMessages: Message[][] = []
  const messageIdToGroupIndex = new Map<Id, number>()
  let currentGroup: Message[] = []

  messages?.forEach((message, index) => {
    const shouldStartNewGroup =
      index === 0 ||
      message.isDeleted ||
      message.replyToMessage ||
      isFirstAttachmentMessage(message.id, messages)

    if (shouldStartNewGroup && currentGroup.length > 0) {
      groupedMessages.push(currentGroup)
      currentGroup = []
    }

    if (message.isDeleted) {
      groupedMessages.push([message])
      messageIdToGroupIndex.set(message.id, groupedMessages.length - 1)
    } else {
      currentGroup.push(message)
      messageIdToGroupIndex.set(message.id, groupedMessages.length)
    }
  })

  if (currentGroup.length > 0) {
    groupedMessages.push(currentGroup)
  }

  return { groupedMessages, messageIdToGroupIndex }
}

export function groupPinnedMessages(
  messages: readonly Message[] | undefined
): GroupedMessageResult {
  const groupedMessages: Message[][] = []
  const messageIdToGroupIndex = new Map<Id, number>()

  messages?.forEach((message, index) => {
    groupedMessages.push([message])
    messageIdToGroupIndex.set(message.id, index)
  })

  return { groupedMessages, messageIdToGroupIndex }
}

export const getGroupedMessageAttachments = (
  messageId: Id,
  messages: Message[] | readonly Message[]
): Attachment[] | undefined => {
  if (messages) {
    const { groupedMessages, messageIdToGroupIndex } = groupMessages(messages)
    const groupIndex = messageIdToGroupIndex.get(messageId)

    if (groupIndex !== undefined) {
      const group = groupedMessages[groupIndex]
      const groupedAttachments: Attachment[] = group
        .map(m => m.attachment)
        .filter(
          (attachment): attachment is Attachment => attachment !== undefined
        )
      return groupedAttachments
    }
  }
  return undefined
}

export const getMessageGroupData = (
  messages: Message[] | readonly Message[] | undefined
): {
  reactions: Reaction[]
  mentions: Mention[]
  attachments: Attachment[]
} => {
  let groupedReactions: Reaction[] = []
  let groupedMentions: Mention[] = []
  const groupedAttachments: Attachment[] = []
  if (messages) {
    messages.forEach(message => {
      if (message.reactions) {
        groupedReactions = groupedReactions.concat(message.reactions)
      }
      if (message.mentions) {
        groupedMentions = groupedMentions.concat(message.mentions)
      }
      if (message.attachment) {
        groupedAttachments.push(message.attachment)
      }
    })
  }
  return {
    reactions: groupedReactions,
    mentions: groupedMentions,
    attachments: groupedAttachments,
  }
}

export const convertNativeAttachmentToFile = async ({
  data,
}: {
  data: ImageChangeEventData
}): Promise<AttachmentFile | undefined> => {
  const base64Data = data.data
  const fileType = data.mime

  if (base64Data && fileType) {
    const fileName = `${Math.random().toString(36).substring(2, 15)}.${
      fileType.split('/')[1]
    }`
    const fileUri = FileSystem.cacheDirectory + fileName

    await FileSystem.writeAsStringAsync(fileUri, base64Data, {
      encoding: FileSystem.EncodingType.Base64,
    })

    if (isImageType(fileType)) {
      const mediaFile: MediaFile = {
        uri: fileUri,
        type: 'media',
        mimeType: fileType,
      }
      Image.getSize(fileUri, (imgWidth, imgHeight) => {
        mediaFile.height = imgHeight
        mediaFile.width = imgWidth
      })
      return mediaFile
    } else if (fileType.startsWith('video')) {
      const video = document.createElement('video')
      video.src = fileUri
      video.addEventListener('loadedmetadata', function () {
        const mediaFile: MediaFile = {
          uri: fileUri,
          height: video.height,
          width: video.width,
          type: 'media',
          mimeType: fileType,
        }
        return mediaFile
      })
    } else if (isPdfType(fileType)) {
      const documentFile: DocumentFile = {
        uri: fileUri,
        type: 'document',
        mimeType: fileType,
      }
      return documentFile
    }
  }
}

function getScrollbarWidth() {
  if (Platform.OS === 'web') {
    // Creating invisible container
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll' // forcing scrollbar to appear
    document.body.appendChild(outer)

    // Creating inner element and placing it in the container
    const inner = document.createElement('div')
    outer.appendChild(inner)

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth

    // Removing temporary elements from the DOM
    document.body.removeChild(outer)

    return scrollbarWidth
  }
  return 0
}

export function useNewsfeedMessageImageStyles({
  width,
  height = DEFAULT_NEWSFEED_IMAGE_HEIGHT,
  isMultiDocument = false,
}: {
  width: number
  height?: number
  isMultiDocument?: boolean
}): { width: number; height: number; maxWidth?: number } {
  const scrollBarWidth = useMemo(() => getScrollbarWidth(), [])
  let maxWidth: number | undefined
  let w = width - NEWSFEED_CARD_PADDING * 2 + (isMultiDocument ? 2.5 : 0)
  if (Platform.OS === 'web') {
    maxWidth = WEB_SCREEN_WIDTH - scrollBarWidth - NEWSFEED_CARD_PADDING * 2
    w = w - scrollBarWidth
  }
  return {
    width: w,
    height,
    maxWidth,
  }
}

export function isConversationHasNewMessage(conversation: Conversation) {
  if (conversation.status?.recentMessage) {
    return (
      conversation.status?.recentMessage?.createdAt >
      (conversation.status?.lastViewedTimestamp ?? 0)
    )
  }
  return false
}

export function isMessageUnread(
  message: Message,
  conversation: Conversation,
  user: User | undefined
) {
  return (
    message.user.id !== user?.id &&
    message.createdAt > (conversation.status?.lastViewedTimestamp ?? 0)
  )
}

export async function appendFileToFormData(
  formData: FormData,
  formFieldName: string,
  { name, uri, mimeType }: AttachmentFileBase
) {
  // Create a safe filename that avoids any special characters
  // that could cause issues with Content-Disposition parsing
  const encodedFilename = name ? encodeFilename(name) : ''

  if (Platform.OS === 'web') {
    if (uri.startsWith('blob:')) {
      // Expo SDK 54+ returns blob: URLs from image picker on web
      const response = await fetch(uri)
      const blob = await response.blob()
      const file = new File([blob], encodedFilename, { type: mimeType })
      formData.append(formFieldName, file)
    } else {
      formData.append(
        formFieldName,
        dataURItoFile(uri, mimeType, encodedFilename)
      )
    }
  } else {
    formData.append(formFieldName, {
      uri,
      type: mimeType,
      name: encodedFilename,
    })
  }
}

/**
 * Encodes a filename for use in Content-Disposition headers
 * @param str The filename to encode
 * @returns The properly encoded filename
 */
export function encodeFilename(str: string): string {
  // Convert the filename to a simpler form that only contains safe characters
  return str.replace(/[^\w.-]/g, '_')
}

export function dataURItoFile(
  dataURI: string,
  mimeType: string,
  fileName: string = ''
): File {
  const binary = atob(dataURI.split(',')[1])
  const array: number[] = []
  for (let i = 0; i < binary.length; i++) {
    array.push(binary.charCodeAt(i))
  }
  const arrayBuffer = new Uint8Array(array)
  return new File([arrayBuffer], fileName, { type: mimeType })
}

export function blobToBase64(blob: Blob) {
  return new Promise((resolve, _) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  })
}

/**
 * Uploads a file to a presigned URL
 * @param presignedUrl The URL to upload to
 * @param fileUri The URI of the file to upload
 * @param mimeType The MIME type of the file
 * @param onProgress Optional callback to report upload progress (0-1)
 * @returns A promise that resolves when the upload is complete
 */
export async function uploadFileToPresignedUrl(
  presignedUrl: string,
  fileUri: string,
  mimeType: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      // For web platforms, use fetch API
      // Get the file content as blob
      let fileBlob: Blob

      // If the URI is already a blob URL, fetch it directly
      if (fileUri.startsWith('blob:')) {
        const response = await fetch(fileUri)
        fileBlob = await response.blob()
      } else {
        // For other URIs (like data URLs), create a blob from them
        const response = await fetch(fileUri)
        fileBlob = await response.blob()
      }

      // For web, we can use XMLHttpRequest to track progress
      if (onProgress) {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()

          // Set up progress tracking
          xhr.upload.addEventListener('progress', event => {
            if (event.lengthComputable) {
              const progress = Math.min(1, event.loaded / event.total)
              onProgress(progress)
            }
          })

          // Set up completion handlers
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              console.log('File uploaded successfully via XMLHttpRequest')
              resolve()
            } else {
              const errorMessage = `File upload failed with status: ${xhr.status}`
              console.warn(errorMessage)
              reject(new Error(errorMessage))
            }
          })

          xhr.addEventListener('error', () => {
            const errorMessage = 'XHR upload failed'
            console.warn(errorMessage)
            reject(new Error(errorMessage))
          })

          xhr.addEventListener('abort', () => {
            const errorMessage = 'XHR upload aborted'
            console.warn(errorMessage)
            reject(new Error(errorMessage))
          })

          // Open and send the request
          xhr.open('PUT', presignedUrl)
          xhr.setRequestHeader('Content-Type', mimeType)
          xhr.send(fileBlob)
        })
      } else {
        // If no progress callback, use simple fetch
        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': mimeType,
          },
          body: fileBlob,
        })

        if (!uploadResponse.ok) {
          const errorMessage = `File upload failed with status: ${uploadResponse.status}`
          console.warn(errorMessage)
          throw new Error(errorMessage)
        } else {
          console.log('File uploaded successfully via fetch API')
        }
      }
    } else {
      // For native platforms, use expo-file-system with createUploadTask for progress tracking
      if (onProgress) {
        // Create an upload task with progress tracking
        const uploadTask = FileSystem.createUploadTask(
          presignedUrl,
          fileUri,
          {
            uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
            headers: {
              'Content-Type': mimeType,
            },
            httpMethod: 'PUT',
          },
          ({ totalBytesSent, totalBytesExpectedToSend }) => {
            const progress = totalBytesSent / (totalBytesExpectedToSend || 1)
            onProgress(Math.min(1, progress))
          }
        )

        try {
          // Start the upload and wait for it to complete
          const uploadResult = await uploadTask.uploadAsync()

          // Check if upload was successful
          if (
            uploadResult &&
            (uploadResult.status < 200 || uploadResult.status >= 300)
          ) {
            const errorMessage = `File upload failed with status: ${uploadResult.status}`
            console.warn(errorMessage)
            throw new Error(errorMessage)
          }
        } catch (error) {
          console.warn('Upload task failed:', error)
          throw error
        }
      } else {
        // If no progress callback, use the regular uploadAsync
        const uploadResult = await FileSystem.uploadAsync(
          presignedUrl,
          fileUri,
          {
            uploadType: FileSystem.FileSystemUploadType.BINARY_CONTENT,
            headers: {
              'Content-Type': mimeType,
            },
            sessionType: FileSystem.FileSystemSessionType.BACKGROUND,
            httpMethod: 'PUT',
          }
        )

        // Check if upload was successful
        if (uploadResult.status < 200 || uploadResult.status >= 300) {
          const errorMessage = `File upload failed with status: ${uploadResult.status}`
          console.warn(errorMessage)
          throw new Error(errorMessage)
        }
      }
    }
  } catch (error) {
    console.error('Error uploading file:', error)
    throw new Error('Failed to upload file. Please try again.')
  }
}

export const handleTextChanged = (
  text: string,
  setText: (text: string) => void,
  characterLimitAlertShown: React.MutableRefObject<boolean>
): void => {
  if (text.length >= MAX_MESSAGE_LENGTH) {
    if (!characterLimitAlertShown.current) {
      informationAlert(
        t('characterLimitExceeded'),
        t('yourMessageExceedsTheCharacterLimit', {
          limit: MAX_MESSAGE_LENGTH,
        })
      )
      characterLimitAlertShown.current = true
    }
    return
  } else {
    characterLimitAlertShown.current = false
  }
  setText(text)
}

// Calculate how many days of messages are currently cached
export function getDaysSinceOldestMessage(oldestEventId: string | undefined) {
  return oldestEventId
    ? Math.floor(
        (Date.now() -
          Number(
            typeof oldestEventId === 'string'
              ? oldestEventId.split('-')[0]
              : oldestEventId
          )) /
          (1000 * 60 * 60 * 24)
      )
    : INITIAL_MESSAGE_FETCH_WINDOW_DAYS
}
